/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unknown-property */
import {
  ActionIcon, Fieldset, FormHandlers, FormSkeleton, HStack, ICON_CLOSE, ICON_EYE_OUTLINE,
  Message,
  Stack,
  useNotifications,
} from '@doveit/bricks';
import React from 'react';
import {
  Company,
  Contact, Invoice, InvoiceReference, PropertyPreview, PropertySearchItem,
} from '../../../providers/api/dtos';
import InvoiceForm, { InvoiceFormModel, InvoiceFormProps } from '../../components/invoice-form/InvoiceForm';
import { createInvoice, updateInvoice } from '../../../providers/api/invoice/invoiceProvider';
import PropertyAutoComplete from '../../../containers/property-autocomplete/PropertyAutoComplete';
import ViewPropertyPreview from '../../../property/containers/view-property-preview/ViewPropertyPreview';
import useContact, { getUseContactRid } from '../../../contact/hooks/use-contact/useContact';
import usePropertyPreview from '../../../property/hooks/use-property-preview/usePropertyPreview';
import {
  ALL_STATUS,
  InvoiceStatus, InvoiceType, PaymentMethod, ReferenceType,
} from '../../../domain/types';
import createOrUpdateContact from '../../../contact/utils/createOrUpdateContact';
import { getTypeFieldOptions } from './utils';
import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import { toInvoiceFormModel } from '../mappers/mappers';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import useAssignmentByPropertyId from '../../../property/hooks/use-assignment-by-property-id/useAssignmentByPropertyId';
import SelectCompany from '../../../company/containers/select-company/SelectCompany';
import useCompany, { getUseCompanyRid } from '../../../company/hooks/use-company/useCompany';
import { CompanyFormModel } from '../../../company/components/company-form/CompanyForm';
import createOrUpdateCompany from '../../../company/utils';
import { getPaymentDateLabel } from '../utils';
import ViewSuggestedContacts from '../../../contact/containers/view-suggested-contacts/ViewSuggestedContacts';
import SelectContact from '../../../contact/containers/select-contact/SelectContact';
import { editInvoiceContactCompleteValidationSchema, editInvoiceContactMinimalValidationSchema } from './EditInvoiceContact.schema';
import { mergeContact } from '../../../contact/utils/mergeContact';
import { contactToContactFormModel } from '../../../contact/utils/mappers';
import * as styles from './UpsertInvoice.style';
import { useMatchMutate } from '../../../utils/swr/swr';
import { ContactStatus } from '../../../contact/containers/select-contact/types';

type Reference =
  | { type: ReferenceType.CONTACT, reference?: Contact, overwriteValues?: Partial<Contact> }
  | { type: ReferenceType.COMPANY, reference?: Company, overwriteValues?: CompanyFormModel };

type UpsertInvoiceDisabledFields = InvoiceFormProps['disabledFields'] & {
  property?: boolean,
  reference?: boolean,
};

interface UpdateInvoiceProps {
  invoice: Invoice,
  toStatus?: InvoiceStatus,
  type?: never,
  assignmentId?: never,
  reference?: never,
}

interface CreateInvoiceProps {
  assignmentId?: number,
  reference?: InvoiceReference,
  type?: InvoiceType,
  invoice?: never,
  toStatus?: never,
}

interface BaseUpsertInvoiceProps extends React.AriaAttributes {
  formRef?: React.MutableRefObject<FormHandlers>,
  hiddenFields?: InvoiceFormProps['hiddenFields'],
  disabledFields?: UpsertInvoiceDisabledFields,
  onFormStatusChanged?: (status: boolean) => void,
  onSuccess?: (invoice: Invoice) => void,
}

export type UpsertInvoiceProps = BaseUpsertInvoiceProps & (CreateInvoiceProps | UpdateInvoiceProps);

export const LOAD_PREFILLED_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati necessari per aggiungere il pagamento.';
export const SAVE_INVOICE_SUCCESS_MESSAGE = 'Pagamento salvato con successo.';
export const SAVE_INVOICE_ERROR_MESSAGE = 'Non è stato possibile salvare il pagamento.';
export const UPDATE_INVOICE_WARNING_MESSAGE = "La tipologia e l'importo non sono modificabili, poiché sono state aggiunte le commissioni.";

const UpsertInvoice: React.FC<UpsertInvoiceProps> = ({
  invoice,
  type,
  toStatus,
  assignmentId: externalAssignmentId,
  reference,
  formRef,
  hiddenFields,
  disabledFields,
  onSuccess,
  onFormStatusChanged,
}) => {
  const matchMutate = useMatchMutate();
  const [selectedProperty, setSelectedProperty] = React.useState<PropertySearchItem | PropertyPreview | null>(null);
  const [selectedReference, setSelectedReference] = React.useState<Reference>();
  const [hasDuplicateContact, setHasDuplicateContact] = React.useState(false);

  const {
    user, userIsAdmin, userIsFinance, mainUserRole,
  } = useRBAC();

  const invoiceReferenceStatus = React.useMemo(() => toStatus ?? invoice?.status ?? InvoiceStatus.DRAFT, [invoice?.status, toStatus]);

  const actualValidationSchema = React.useMemo(() => {
    if (userIsAdmin || userIsFinance) {
      return editInvoiceContactMinimalValidationSchema;
    }
    return editInvoiceContactCompleteValidationSchema;
  }, [userIsAdmin, userIsFinance]);

  const assignmentId = invoice?.assignmentId || externalAssignmentId;

  const contactId = React.useMemo(() => {
    if (reference?.type === ReferenceType.CONTACT) {
      return reference.id;
    }

    return invoice?.reference.type === ReferenceType.CONTACT ? invoice.reference.id : undefined;
  }, [invoice?.reference.id, invoice?.reference.type, reference?.id, reference?.type]);

  const companyId = React.useMemo(() => {
    if (reference?.type === ReferenceType.COMPANY) {
      return reference.id;
    }

    return invoice?.reference.type === ReferenceType.COMPANY ? invoice.reference.id : undefined;
  }, [invoice?.reference.id, invoice?.reference.type, reference?.id, reference?.type]);

  const {
    data: assignment,
    isLoading: isAssignmentLoading,
    error: assignmentError,
  } = useAssignmentById(assignmentId);

  const {
    data: assignmentByProperty,
    isLoading: isAssignmentByPropertyLoading,
    error: assignmentByPropertyError,
  } = useAssignmentByPropertyId(!assignmentId ? selectedProperty?.id : undefined);

  const actualAssignment = React.useMemo(() => assignment || assignmentByProperty, [assignment, assignmentByProperty]);

  const {
    data: initialContact,
    isLoading: isInitialContactLoading,
    error: initialContactError,
  } = useContact(contactId);

  const {
    data: initialCompany,
    isLoading: isInitialCompanyLoading,
    error: initialCompanyError,
  } = useCompany(companyId);

  const {
    data: initialProperty,
    isLoading: isInitialPropertyLoading,
    error: initialPropertyError,
  } = usePropertyPreview(assignment?.propertyId);

  /*  SWR non richiama onSuccess quando i dati provengono dalla cache (viene eseguito solo dopo un refetch).
  *   Usiamo quindi un useEffect per eseguire i setState in caso di dati già presenti in cache.
  */
  React.useEffect(() => {
    if (initialContact) {
      setSelectedReference({
        type: ReferenceType.CONTACT,
        reference: initialContact,
      });
    }
  }, [initialContact]);

  React.useEffect(() => {
    if (initialCompany) {
      setSelectedReference({
        type: ReferenceType.COMPANY,
        reference: initialCompany,
      });
    }
  }, [initialCompany]);

  React.useEffect(() => {
    if (initialProperty) {
      setSelectedProperty(initialProperty);
    }
  }, [initialProperty]);

  const [isSaving, setIsSaving] = React.useState(false);
  const { addSuccess, addError } = useNotifications();

  const typeOptions = React.useMemo(
    () => getTypeFieldOptions(selectedProperty?.status, actualAssignment?.isCollected),
    [actualAssignment?.isCollected, selectedProperty?.status],
  );

  const initialValues = React.useMemo(() => {
    if (invoice) {
      return ({
        ...toInvoiceFormModel(invoice),
        vat: invoice.vat?.toString() || (toStatus && (invoice.type === InvoiceType.PENALTY ? '0' : '22')) || '',
      });
    }

    const initialValuesForUpdate: Partial<InvoiceFormModel> = {
      type: typeOptions.some(({ value }) => value === type) ? type : undefined,
    };

    return initialValuesForUpdate;
  }, [invoice, toStatus, type, typeOptions]);

  const internalDisabledFields: UpsertInvoiceDisabledFields = React.useMemo(() => ({
    ...disabledFields,
    type: disabledFields?.type || (!!type && !!initialValues?.type) || (invoice?.commissions && invoice.commissions.length > 0),
    value: disabledFields?.value || (invoice?.commissions && invoice.commissions.length > 0),
    reference: disabledFields?.reference || !!invoice,
    property: disabledFields?.property || !!invoice,
  }), [disabledFields, initialValues, invoice, type]);

  const disabledFieldMessage = React.useMemo(() => (invoice?.commissions && invoice.commissions.length > 0
    ? UPDATE_INVOICE_WARNING_MESSAGE
    : undefined), [invoice]);

  const paymentDateLabel = React.useMemo(() => getPaymentDateLabel(toStatus), [toStatus]);

  const isContactValid = React.useMemo(
    () => selectedReference?.type === ReferenceType.CONTACT
      && actualValidationSchema.isValidSync(contactToContactFormModel(mergeContact(selectedReference?.reference, selectedReference!.overwriteValues))),
    [actualValidationSchema, selectedReference],
  );

  const isFormDisabled = React.useMemo(() => {
    if (!selectedReference || !selectedProperty) return true;

    if (selectedReference.type === ReferenceType.COMPANY) return false;

    // Disable form if contact is invalid or if there's a duplicate contact
    return !isContactValid || hasDuplicateContact;
  }, [isContactValid, selectedProperty, selectedReference, hasDuplicateContact]);

  const clearPropertySelection = React.useCallback(
    () => setSelectedProperty(null),
    [],
  );

  React.useEffect(() => onFormStatusChanged?.(isFormDisabled), [isFormDisabled, onFormStatusChanged]);

  const onContactChange = React.useCallback((status: ContactStatus, existingContact?: Contact, overwriteValues?: Partial<Contact>) => {
    setSelectedReference((existingContact || overwriteValues) ? {
      reference: existingContact,
      type: ReferenceType.CONTACT,
      overwriteValues,
    } : undefined);
    setHasDuplicateContact(status === 'DUPLICATE');
  }, []);

  const onCompanyChange = React.useCallback((company?: Company, search?: CompanyFormModel) => {
    setSelectedReference((company || search) ? {
      reference: company,
      type: ReferenceType.COMPANY,
      overwriteValues: search,
    } : undefined);
  }, []);

  const onSubmit = React.useCallback(async (values: InvoiceFormModel) => {
    if (!selectedReference || !actualAssignment?.id) return;

    setIsSaving(true);

    try {
      const savedReference = selectedReference.type === ReferenceType.CONTACT
        ? await createOrUpdateContact(selectedReference.overwriteValues, selectedReference.reference)
        : await createOrUpdateCompany(selectedReference.reference, selectedReference.overwriteValues);

      if (selectedReference.type === ReferenceType.COMPANY && savedReference.id) {
        await matchMutate(getUseCompanyRid(savedReference.id));
      }

      if (selectedReference.type === ReferenceType.CONTACT && savedReference.id && selectedProperty?.id) {
        await matchMutate(getUseContactRid(savedReference.id.toString()));
      }

      const upsertedInvoice = !invoice
        ? await createInvoice({
          assignmentId: actualAssignment.id,
          status: InvoiceStatus.DRAFT,
          reference: {
            type: selectedReference.type,
            id: `${savedReference.id}`,
          },
          type: values.type as InvoiceType,
          value: Number.parseFloat(values.value!),
          paidValue: values.paidValue ? Number.parseFloat(values.paidValue) : undefined,
          vat: values.vat ? Number.parseFloat(values.vat) : undefined,
          paymentDate: values.paymentDate,
          paymentMethod: values.paymentMethod ? values.paymentMethod as PaymentMethod : undefined,
          commissions: [],
          notes: [],
        })
        : await updateInvoice(invoice.id!, {
          ...invoice,
          status: toStatus || invoice.status,
          assignmentId: actualAssignment.id,
          reference: {
            type: selectedReference.type,
            id: `${savedReference.id}`,
          },
          type: values.type as InvoiceType,
          value: Number.parseFloat(values.value!),
          paidValue: values.paidValue ? Number.parseFloat(values.paidValue) : undefined,
          vat: values.vat ? Number.parseFloat(values.vat) : undefined,
          paymentDate: values.paymentDate,
          paymentMethod: values.paymentMethod ? values.paymentMethod as PaymentMethod : undefined,
          proformaInvoiceId: values.proformaInvoiceId?.trim() || undefined,
          invoiceId: values.invoiceId?.trim() || undefined,
          notes: values.notes?.trim() ? [{
            author: user.name,
            text: values.notes.trim(),
            role: mainUserRole,
            date: new Date().toISOString(),
          }] : [],
        });

      setIsSaving(false);
      addSuccess(SAVE_INVOICE_SUCCESS_MESSAGE);
      onSuccess?.(upsertedInvoice);
    } catch (error) {
      setIsSaving(false);
      addError(SAVE_INVOICE_ERROR_MESSAGE);
    }
  }, [actualAssignment?.id, addError, addSuccess, invoice, mainUserRole, matchMutate, onSuccess, selectedProperty?.id, selectedReference, toStatus, user.name]);

  if (initialPropertyError || initialContactError || initialCompanyError || assignmentError || assignmentByPropertyError) {
    return (
      <Message
        type="critical"
        message={LOAD_PREFILLED_DATA_ERROR_MESSAGE}
      />
    );
  }

  if (isInitialPropertyLoading || isInitialContactLoading || isInitialCompanyLoading || isAssignmentLoading || isAssignmentByPropertyLoading) {
    return <FormSkeleton aria-label="form-skeleton" />;
  }

  return (
    <Stack gap={300}>
      <Fieldset legend="Dati immobile">
        {!selectedProperty && (
          <PropertyAutoComplete
            aria-label="Seleziona un immobile"
            placeholder="Cerca immobile"
            status={ALL_STATUS}
            onChange={clearPropertySelection}
            onSuggestionSelected={setSelectedProperty}
            maxSuggestions={10}
          />
        )}
        {selectedProperty && (
          <ViewPropertyPreview
            property={selectedProperty}
            primaryActions={[
              <ActionIcon
                icon={{ path: ICON_EYE_OUTLINE }}
                label="Visualizza immobile"
                aria-label="Visualizza la pagina dell'immobile"
                href={`/properties/${selectedProperty.id}`}
                target="_blank"
                size="S"
              />,
              !internalDisabledFields?.property && (
                <ActionIcon
                  icon={{ path: ICON_CLOSE }}
                  label="Deseleziona l'immobile"
                  aria-label="Annulla la selezione dell'immobile"
                  onClick={clearPropertySelection}
                  size="S"
                />
              ),
            ]}
          />
        )}
      </Fieldset>

      <Fieldset legend="Dati intestatario">
        {
          /*
            I wrapper div con flex condizionale permettono ai componenti di:
              - Occupare solo lo spazio necessario quando mostrano il bottone di ricerca o aggiunta ('0 0 auto')
              - Espandersi al 100% quando un contatto o una persona giuridica è selezionata ('1 1 100%')
              Questo consente ai due bottoni "Cerca o aggiungi contatto" e "Cerca o aggiungi persona giuridica" di stare sulla stessa riga su desktop,
              ma di andare correttamente a capo su mobile quando necessario.
          */
        }
        <HStack gap={200}>
          {(!selectedReference || selectedReference?.type === ReferenceType.CONTACT) && (
            <div css={selectedReference?.type === ReferenceType.CONTACT ? styles.referenceWrapperExpanded : styles.referenceWrapperCollapsed}>
              <SelectContact
                onContactChange={onContactChange}
                initialContact={selectedReference?.reference}
                showClearContact={!internalDisabledFields?.reference}
                editContactSchema={actualValidationSchema}
                editContactRequiredFields={{
                  name: true,
                  phoneNumber: false,
                  email: true,
                  fiscalCode: true,
                  address: true,
                  notes: false,
                }}
              />
            </div>
          )}

          {(!selectedReference || selectedReference?.type === ReferenceType.COMPANY) && (
            <div css={selectedReference?.type === ReferenceType.COMPANY ? styles.referenceWrapperExpanded : styles.referenceWrapperCollapsed}>
              <SelectCompany
                onChange={onCompanyChange}
                company={selectedReference?.reference}
                canChange={!internalDisabledFields?.reference}
              />
            </div>
          )}
        </HStack>
      </Fieldset>

      {!selectedReference && selectedProperty?.id && (
        <ViewSuggestedContacts
          reference={{
            id: `${selectedProperty.id}`,
            type: ReferenceType.PROPERTY,
          }}
          onSelect={({ contact }) => onContactChange('OK', contact)}
        />
      )}

      <InvoiceForm
        innerRef={formRef}
        initialValues={initialValues}
        typeOptions={typeOptions}
        invoiceStatus={invoiceReferenceStatus}
        hiddenFields={hiddenFields}
        disabled={isFormDisabled}
        onSubmit={onSubmit}
        loading={isSaving}
        disabledFields={internalDisabledFields}
        disabledFieldsInfoMessage={disabledFieldMessage}
        paymentDateLabel={paymentDateLabel}
      />
    </Stack>
  );
};

export default UpsertInvoice;
